// Document-level hardening for the sandboxed email iframe, split out of
// EmailBody so it can be unit-tested: this logic fails OPEN when it is wrong
// (the iframe simply fetches remote content with no policy) and it has been got
// wrong twice, so it is worth pinning.

// Containment only — deliberately NOT a restyle of the message. An iframe is a
// separate document, so the host page's rules cannot reach it: without these the
// body renders at the browser's bare defaults (Times, 8px body margin) and, more
// importantly, a wide image or table overflows the preview pane the moment the
// user opts into remote images. Everything here is either a constraint or a
// neutral default; paragraph and list spacing is left alone so the preview still
// reflects what a recipient's client will do with the message.
//
// Allowed by the `style-src 'unsafe-inline'` already in the policy below.
const baseStyles =
  '<style>' +
  'body{margin:0;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;overflow-wrap:anywhere}' +
  'img{max-width:100%;height:auto}' +
  'table{max-width:100%}' +
  '</style>';

// Opt-in second stylesheet for bodies authored in the CRM's Quill composer
// (mass-mail campaigns). Quill serializes alignment/indent as `ql-*` classes
// and relies on its editor stylesheet for zero block margins, so without these
// the preview shows browser defaults (1em paragraph gaps doubled by Quill's
// `<p><br></p>` blank lines) and drops alignment entirely. The padding gives
// the message breathing room inside the iframe's own canvas. Inbound portal
// mail keeps the neutral containment-only defaults above.
const quillStyles =
  '<style>' +
  'body{padding:12px 16px;line-height:1.42}' +
  'p,h1,h2,h3,h4,h5,h6,ol,ul,pre,blockquote{margin:0;padding:0}' +
  'ol,ul{padding-left:1.5em}' +
  'blockquote{border-left:4px solid #ccc;padding-left:16px}' +
  '.ql-align-center{text-align:center}' +
  '.ql-align-right{text-align:right}' +
  '.ql-align-justify{text-align:justify}' +
  Array.from(
    { length: 8 },
    (_, i) => `.ql-indent-${i + 1}{padding-left:${(i + 1) * 3}em}`,
  ).join('') +
  '</style>';

// Canvas colours for the CRM's dark mode. The iframe is its own document, so
// the host theme does not reach it and it would stay a white sheet inside a
// dark pane. Only meaningful for Quill-authored bodies: the composer draws
// them on the dark input surface, so this is what the author saw, and the
// only fixed-colour block they can insert (the listing card) carries its own
// white background. Inbound portal mail keeps its white canvas: its inline
// colours assume one. Callers pass resolved colour values, not CSS variables,
// which the iframe cannot see either.
export interface DarkScheme {
  background: string;
  color: string;
}

const darkSchemeTags = ({ background, color }: DarkScheme): string =>
  '<meta name="color-scheme" content="dark">' +
  `<style>body{background:${background};color:${color}}</style>`;

// Emitted before everything else so the parser applies them before any body
// resource starts fetching. CSP controls passive fetches; `<base
// target="_blank">` + sandbox="" (no allow-popups) turns every link click into
// a no-op so trackers can't fire via clicks either, unless the host opts into
// `allowLinks`, where the same base opens them in a new tab.
const headTags = (
  allowRemoteImages: boolean,
  quillContent: boolean,
  darkScheme: DarkScheme | null,
): string =>
  `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:${
    allowRemoteImages ? ' https: http:' : ''
  }; style-src 'unsafe-inline'">` +
  // Once the reader opts into remote images, the requests must not carry the
  // CRM origin to the sender's tracker.
  '<meta name="referrer" content="no-referrer">' +
  '<base target="_blank">' +
  baseStyles +
  (quillContent ? quillStyles : '') +
  (darkScheme != null ? darkSchemeTags(darkScheme) : '');

// Deliberately does NOT look for the document's own `<head>`. Any regex that
// hunts for one is matching a parser's language with a non-parser, and every
// mismatch fails OPEN. Shapes that defeated the search approach, all reachable
// from inbound portal mail: `<header>` (a `[^>]*` class eats the `er`), a
// `<head>` inside a `--!>`-terminated comment, inside an attribute value
// (`<div title="<head >">`), inside rawtext (`<textarea><head></textarea>`),
// and any document with content before its `<head>`, where the parser has
// already opened `<body>` and ignores the pragma per HTML §4.2.5.3.
//
// Prepending needs none of that. Before `<html>`/`<head>` the parser is in the
// "before head" insertion mode, so it creates the implicit head and these
// become its first children — true for a bare fragment as much as for a full
// document. The doctype is kept leading so the iframe stays in standards mode;
// putting content before it would flip the table box model to quirks, which
// matters for email templates.
// `[^>]*` is deliberate, and it matches the tokenizer: a `>` ends the DOCTYPE
// token even inside a quoted public/system identifier. HTML §13.2.5.65 — in the
// "DOCTYPE system identifier (double-quoted)" state a `>` is the
// `abrupt-doctype-system-identifier` parse error, which sets force-quirks,
// switches to the data state and EMITS the token. Same for the public and
// single-quoted variants.
//
// So consuming past that `>` is what breaks it: the identifier's tail (`">`)
// would become character data sitting before the injected meta, and character
// data in "before html" / "before head" creates and then closes the implicit
// head — leaving the meta in `<body>`, where the CSP pragma is ignored. Fail
// open again, which is exactly what this file exists to avoid.
//
// Nothing is lost by stopping at the first `>`: real doctypes (HTML5, XHTML 1.0,
// HTML 4.01, XHTML 1.1) carry no `>` inside their identifiers, so they still
// match whole and keep standards mode. In the one case where a wider regex would
// differ, the tokenizer has already set force-quirks — standards mode is
// unreachable there anyway, so trading the CSP for it would be the wrong side of
// that bargain.
const LEADING_DOCTYPE = /^\s*<!doctype[^>]*>/i;

export const injectHead = (
  html: string,
  allowRemoteImages: boolean,
  quillContent = false,
  darkScheme: DarkScheme | null = null,
): string => {
  const tags = headTags(allowRemoteImages, quillContent, darkScheme);
  const doctype = LEADING_DOCTYPE.exec(html);
  return doctype != null
    ? html.slice(0, doctype[0].length) + tags + html.slice(doctype[0].length)
    : tags + html;
};
